import { toOptionalString } from "features/shared/lib/formatters";
import { getFieldKindStrategy } from "features/shared/lib/technical-config-tab/fieldKindStrategies";
import {
  buildDefaultFormState,
  mapConfigToFormState as mapConfigToFormStateFromFields,
  mapFormStateToPayload as mapFieldsToPayload,
} from "features/shared/lib/technical-config-tab/formStateBuilders";

import type {
  RoomCatalogCreatePayload,
  RoomCatalogFormState,
  RoomConfigCreatePayload,
  RoomConfigFormState,
  RoomConfigSavePayload,
  RoomTypeConfig,
  RoomTypeGroup,
} from "../model/types";

import {
  type ActiveFieldDefinition,
  getActiveFieldDefinitions,
  getTabFieldDefinitions,
  isTabField,
  type TabFieldDefinition,
} from "./roomConfigFieldDefinitions";

const getConfigFieldValue = (config: RoomTypeConfig | undefined, key: string): unknown => {
  if (!config) return undefined;
  return Reflect.get(config, key);
};

const isCompleteCatalogCreatePayload = (
  payload: Partial<RoomCatalogCreatePayload>,
  fields: readonly ActiveFieldDefinition[]
): payload is RoomCatalogCreatePayload => fields.every((field) => field.key in payload);

export const defaultRoomConfigFormState = (): RoomConfigFormState => buildDefaultFormState(getTabFieldDefinitions());

export const defaultCatalogFormState = (): RoomCatalogFormState => buildDefaultFormState(getActiveFieldDefinitions());

export const mapConfigToFormState = (config?: null | RoomTypeConfig): RoomConfigFormState =>
  mapConfigToFormStateFromFields({
    config: config ?? undefined,
    fields: getTabFieldDefinitions(),
    getRawValue: (source, field) => getConfigFieldValue(source, field.key),
  });

const mapFieldFormToPayloadValue = (
  field: TabFieldDefinition,
  value: boolean | string
): boolean | null | number | string => getFieldKindStrategy(field.kind).formToApiValue(value);

const assignCatalogPayloadValue = (
  payload: Partial<RoomCatalogCreatePayload>,
  field: ActiveFieldDefinition,
  formState: RoomCatalogFormState
): void => {
  if (isTabField(field)) {
    Object.assign(payload, { [field.key]: mapFieldFormToPayloadValue(field, formState[field.key]) });
    return;
  }

  if (field.kind !== "string") return;

  const formValue = formState[field.key];
  if (typeof formValue !== "string") return;

  Object.assign(payload, { [field.key]: toOptionalString(formValue) });
};

export const mapFormStateToPayload = (formState: RoomConfigFormState): RoomConfigSavePayload =>
  mapFieldsToPayload(formState, getTabFieldDefinitions());

export const mapDetailConfigToSavePayload = (config: RoomTypeConfig, color?: string): RoomConfigSavePayload => ({
  ...mapFormStateToPayload(mapConfigToFormState(config)),
  ...(color || config.color ? { color: color ?? config.color } : {}),
  ...(config.schedules ? { schedules: config.schedules } : {}),
});

export const mapCatalogFormStateToPayload = (formState: RoomCatalogFormState): RoomCatalogCreatePayload => {
  const fields = getActiveFieldDefinitions();
  const payload: Partial<RoomCatalogCreatePayload> = {};

  fields.forEach((field) => assignCatalogPayloadValue(payload, field, formState));

  if (!isCompleteCatalogCreatePayload(payload, fields)) {
    throw new Error("Failed to map catalog create payload");
  }

  return payload;
};

export const mapGroupToCreatePayload = (group: RoomTypeGroup): RoomConfigCreatePayload => ({
  ...(group.color ? { color: group.color } : {}),
  ...(group.roomTypeName ? { saveAsRoomTypeName: group.roomTypeName } : {}),
});
